import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    IconButton,
    Chip,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import { getMontonioApiKeys, updateMontonioApiKeys, deleteMontonioApiKeys } from '../api/montonioApi';

const MontonioApiModal = ({ open, onClose, affiliateId, affiliateName }) => {
    const [accessKey, setAccessKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hasExistingKeys, setHasExistingKeys] = useState(false);

    // Load existing API keys status when modal opens
    useEffect(() => {
        if (open && affiliateId) {
            loadExistingKeys();
        }
    }, [open, affiliateId]);

    const loadExistingKeys = async () => {
        setLoadingInitial(true);
        setError('');
        try {
            const response = await getMontonioApiKeys(affiliateId);
            if (response.success) {
                setHasExistingKeys(response.hasKeys);
            }
        } catch (error) {
            setError('Error loading existing keys status');
        } finally {
            setLoadingInitial(false);
        }
    };

    // Reset state when modal closes
    const handleClose = () => {
        setAccessKey('');
        setSecretKey('');
        setShowSecretKey(false);
        setError('');
        setSuccess('');
        setHasExistingKeys(false);
        onClose();
    };

    // Save API keys
    const handleSave = async () => {
        setError('');
        setSuccess('');

        // Validation
        if (!accessKey.trim()) {
            setError('Please enter an Access Key');
            return;
        }

        if (!secretKey.trim()) {
            setError('Please enter a Secret Key');
            return;
        }

        setLoading(true);

        try {
            const response = await updateMontonioApiKeys(
                affiliateId,
                accessKey.trim(),
                secretKey.trim()
            );

            if (response.success) {
                setSuccess('Montonio API keys saved successfully!');
                setHasExistingKeys(true);

                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setError(response.message || 'Failed to save API keys');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error saving API keys');
        } finally {
            setLoading(false);
        }
    };

    // Delete API keys
    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete Montonio API keys? This will disable payment processing for this affiliate.')) {
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await deleteMontonioApiKeys(affiliateId);

            if (response.success) {
                setSuccess('Montonio API keys deleted successfully!');
                setHasExistingKeys(false);
                setAccessKey('');
                setSecretKey('');

                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setError(response.message || 'Failed to delete API keys');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error deleting API keys');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon color="primary" />
                        <Typography variant="h6">Montonio Payment Configuration</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {affiliateName}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {loadingInitial ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter your Montonio API credentials to enable payment processing for this affiliate.
                        </Typography>

                        {hasExistingKeys && (
                            <Box sx={{ mb: 2 }}>
                                <Chip
                                    label="API Keys Configured"
                                    color="success"
                                    size="small"
                                    icon={<KeyIcon />}
                                />
                            </Box>
                        )}

                        {/* Access Key Field */}
                        <TextField
                            fullWidth
                            label="Access Key"
                            value={accessKey}
                            onChange={(e) => setAccessKey(e.target.value)}
                            disabled={loading}
                            placeholder="Enter your Montonio Access Key"
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyIcon sx={{ color: 'action.active' }} />
                                    </InputAdornment>
                                )
                            }}
                            helperText="Your Montonio Access Key (UUID format)"
                        />

                        {/* Secret Key Field */}
                        <TextField
                            fullWidth
                            label="Secret Key"
                            type={showSecretKey ? 'text' : 'password'}
                            value={secretKey}
                            onChange={(e) => setSecretKey(e.target.value)}
                            disabled={loading}
                            placeholder="Enter your Montonio Secret Key"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyIcon sx={{ color: 'action.active' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowSecretKey(!showSecretKey)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            helperText="Your Montonio Secret Key (will be encrypted)"
                        />

                        {/* Info box */}
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Security:</strong> Your Secret Key will be encrypted before being stored in the database.
                            </Typography>
                        </Alert>

                        {/* Success message */}
                        {success && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {/* Error message */}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                {!loadingInitial && (
                    <>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>

                        {hasExistingKeys && (
                            <Button
                                onClick={handleDelete}
                                color="error"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
                            >
                                Delete
                            </Button>
                        )}

                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={loading || !accessKey.trim() || !secretKey.trim()}
                            startIcon={loading ? <CircularProgress size={16} /> : <KeyIcon />}
                        >
                            {hasExistingKeys ? 'Update' : 'Save'} Keys
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default MontonioApiModal;

